import { IGroupWithIds } from "@app/main/components/resourceManagement/groups/_models/IGroupWithIds";
import { DateService } from "@app/services/date.service";
import { IAutocompleteMatch } from "@directives/_forms/autocomplete/autocomplete.component";
import { IPhoneNumberOutput } from "@directives/_forms/smsInput/smsInput.interfaces";
import { DayOfWeek } from "@enums/DayOfWeek";
import { NotificationRecipientType } from "@enums/NotificationRecipientType";
import { NotificationRecordStatus } from "@enums/NotificationRecordStatus";
import { ReasonCode } from "@enums/ReasonCode";
import { ResourceType } from "@enums/ResourceType";
import { ICombinedPreferences } from "@interfaces/ICombinedPreferences";
import { IKeyValuePair } from "@interfaces/IKeyValuePair";
import { IUser } from "@interfaces/IUser";
import { IVehicle } from "@interfaces/IVehicle";
import { EnumService } from "@services/enum.service";
import { GroupService } from "@services/group.service.ajs";
import { Currency, DistanceMeasurement, PreferencesService, TemperatureMeasurement, VolumeMeasurement } from "@services/preferences.service";
import { SessionService } from "@services/session.service";
import { ITranslationLocale, TranslationService } from "@services/translation.service";
import { UnitConversionService, UnitConversionType } from "@services/unitConversion.service";
import { UsersService } from "@services/users.service";
import { ValidationService } from "@services/validation.service";
import { VehicleService } from "@services/vehicle.service";
import * as angular from "angular";
import copy from "fast-copy/dist/fast-copy.esm.js";
import { SettingsName } from "src/app/class/SettingsName";
import { ResendIntervalType } from "src/app/main/modules/alerts/_enums/alertEditor.enums";
import { AlertRuleStatus } from "src/app/main/modules/alerts/_enums/AlertRuleStatus.enums";
import { AlertCategory, AlertPreferenceType, RecipientDeliveryType, RecipientType, ResourceFilterType, TriggerValueOperator, VehicleAlertSubCategory } from "src/app/main/modules/alerts/_enums/alerts.enums";
import { ITimeZoneInfo } from "src/app/userPreferences/model/ITimeZoneInfo";
import { IAlertFilterCriteria, IAlertRule, IAlertRuleRecipient } from "../../_models/alerts.interfaces";
import { AlertsService, IAlertCategoryTrigger } from "../../_services/alerts.service";
import './alertEditor.component.scss';

interface ITimeRangeSelection {
    from?: IHourMinute;
    to?: IHourMinute;
}

interface IHourMinute {
    hour?: number;
    minute?: number;
}

interface IRecipientValidation {
    valid?: boolean;
    error?: string;
}

interface IConversationalSegment {
    id?: number;
    label: string;
    styleClass?: string;
}

interface IComponentBindings {
    alertRule: IAlertRule;
    autoLoadedRecordId: number;
    onAlertEditorCancelled: () => void;
    onAlertEditorSaved: () => void;
}


class AlertEditorComponentController implements angular.IComponentController, IComponentBindings {

    // bindings
    alertRule: IAlertRule;
    autoLoadedRecordId: number;
    onAlertEditorCancelled: () => void;
    onAlertEditorDeleted: () => void;
    onAlertEditorSaved: () => void;

    dirtyAlertRule: IAlertRule;

    // misc
    alertForm: any;

    // enums
    AlertCategory = AlertCategory;
    VehicleSubAlertCatagory = VehicleAlertSubCategory;
    ReasonCode = ReasonCode;
    ResourceType = ResourceType;
    DayOfWeek = DayOfWeek;
    daysOfWeek: IKeyValuePair<typeof DayOfWeek>[];
    recipientTypes: IKeyValuePair<typeof RecipientType>[] = [];
    recipientDeliveryTypes: IKeyValuePair<typeof RecipientDeliveryType>[] = [];
    distanceUnitTypes: IKeyValuePair<typeof DistanceMeasurement>[] = [];
    temperatureUnitTypes: IKeyValuePair<typeof TemperatureMeasurement>[] = [];
    volumeUnitTypes: IKeyValuePair<typeof VolumeMeasurement>[] = [];
    currencyTypes: IKeyValuePair<typeof Currency>[] = [];

    alertCategories: any[] = [];
    reasonCodes: any[] = [];

    // prepared for DayOfWeekSelector component
    allDays: DayOfWeek[] = [DayOfWeek.Sunday, DayOfWeek.Monday, DayOfWeek.Tuesday, DayOfWeek.Wednesday, DayOfWeek.Thursday, DayOfWeek.Friday, DayOfWeek.Saturday];
    weekDays: DayOfWeek[] = [DayOfWeek.Monday, DayOfWeek.Tuesday, DayOfWeek.Wednesday, DayOfWeek.Thursday, DayOfWeek.Friday];
    weekendDays: DayOfWeek[] = [DayOfWeek.Sunday, DayOfWeek.Saturday];

    // raw data
    groups: IGroupWithIds[] = [];
    users: IUser[] = [];
    vehicles: IVehicle[] = [];
    existingAlertRules: IAlertRule[] = [];
    locales: ITranslationLocale[];
    timezones: ITimeZoneInfo[];

    // spinners
    isLoadingAlertNotifications = false;
    isLoadingAlertRecords: boolean = false;
    isSavingAlertRule: boolean = false;
    isFormReady: boolean = false;

    recipientAddError: string;
    enableDelay: boolean = false;
    resendMinuteInterval: number;
    resendIntervalType: ResendIntervalType = ResendIntervalType.Minutes;
    alertRuleRecordVehicles: any[] = [];
    timeRange: ITimeRangeSelection;
    inputTypeFilter: IAlertFilterCriteria = null;
    subCategoryUnits: IKeyValuePair<any>[] = [];

    alertExclusionDays: boolean[] = [];

    newRecipient: IAlertRuleRecipient = {
        type: RecipientType.Group
    };

    weekDaysDescriptor: string;
    currentTabIndex: number = 0;

    alertCategoryTriggers: IAlertCategoryTrigger[] = [];

    selectedHistoryDate: Date;
    delayResendIcon: string;

    filteredStartMinuteOfDay: number;
    filteredEndMinuteOfDay: number;
    timeRangeValid: boolean;

    newRecipientSmsInput: string;

    defaultDelayValue: number = 10;
    defaultResendValue: number = 60;

    conversationalSegments: IConversationalSegment[] = [];
    conversationalSegmentsString: string;

    alertPreferences: string[] = [];

    alertPreferenceTypes: AlertPreferenceType;

    combinedPreferences: ICombinedPreferences;
    languagePreferenceValue: number = AlertPreferenceType.Language;
    timezonePreferenceValue: number = AlertPreferenceType.Timezone;

    validationError: string;

    alertIsActive: boolean;

    get errorMessage(): string {
        if (this.dirtyAlertRule?.Name === undefined) {
            return this.translationService.t("AlertEditor.errorCompleteRequiredFields")
        }

        if (this.dirtyAlertRule?.Recipients?.length === 0) {
            return this.translationService.t("AlertEditor.errorAddRecipientRequired");
        }

        if (this.isSavingAlertRule) {
            return this.translationService.t("AlertEditor.SavingAlertRule");
        }

        return this.translationService.t("AlertEditor.UnknownAlertError");
    } 

    get canSaveRule(): boolean {

        return !!this.dirtyAlertRule?.Name
            && this.dirtyAlertRule.Recipients?.length >= 1
            && !this.alertForm.$invalid
            && !this.isSavingAlertRule;
    }

    static $inject = ["$q", "alertsService", "dateService", "groupService", "usersService", "vehicleService", "unitConversionService", "translationService", "validationService", "sessionService", "preferencesService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly alertsService: AlertsService,
        private readonly dateService: DateService,
        private readonly groupService: GroupService,
        private readonly usersService: UsersService,
        private readonly vehicleService: VehicleService,
        private readonly unitConversionService: UnitConversionService,
        private readonly translationService: TranslationService,
        private readonly validationService: ValidationService,
        private readonly sessionService: SessionService,
        private readonly preferencesService: PreferencesService) {
    }

    $onInit() {
        this.alertCategoryTriggers = this.alertsService.createAlertTriggerMappings();
        this.setupEnumArrays();
        this.selectedHistoryDate = new Date();
        this.combinedPreferences = this.sessionService.getItem<ICombinedPreferences>(SettingsName.UserOptions);
        this.setupRule();

    }

    onAlertCategoryChanged(): void {
        this.resetDelayAndResendIfNeeded();
        this.updateConversationalSegments();
        this.setDefaultValueOfCategoryTrigger();
        this.setInputTypeFilter();
        this.setUIAlertPreferences();
    }

    onAlertCategoryTriggerChanged(): void {
        if (this.dirtyAlertRule.SubCategory == VehicleAlertSubCategory.PlotReason) {
            this.dirtyAlertRule.TriggerValue = ReasonCode.IgnitionOn;
            this.dirtyAlertRule.TriggerValueOperator = TriggerValueOperator.Equals;
        } else {
            this.dirtyAlertRule.TriggerValue = undefined;
        }

        this.resetDelayAndResendIfNeeded();
        this.updateConversationalSegments();
        this.getSubCategoryUnitsList();
    }

    onEnableDelayChanged(enabled: boolean): void {
        this.dirtyAlertRule.FirstNotificationDelay = (!enabled) ? null : this.dirtyAlertRule?.FirstNotificationDelay || this.defaultDelayValue;

        this.setLocalDelayVars();
        this.updateConversationalSegments();
    }

    onDelayChanged(): void {
        this.updateConversationalSegments();
    }

    onEnableResendAlertsChanged(enabled: boolean): void {
        this.dirtyAlertRule.ResendMinuteInterval = (!enabled) ? null : this.dirtyAlertRule?.ResendMinuteInterval || this.defaultResendValue;

        this.setLocalResendVars();
        this.updateConversationalSegments();
    }

    onResendMinuteIntervalChanged(): void {
        this.updateResendMinuteInterval(this.resendIntervalType, this.resendMinuteInterval);
    }

    onResendIntervalTypeChanged(): void {
        this.updateResendMinuteInterval(this.resendIntervalType, this.resendMinuteInterval);
    }

    onSmsOutputChanged($event: IPhoneNumberOutput): void {
        this.newRecipient.Custom = $event?.internationalized;
    }

    changeTabIndex(tabIndex: number): void {
        this.currentTabIndex = tabIndex;
    }

    validateAlertName(value: string): boolean {
        if (!value) {
            return false;
        }

        return !this.hasForDuplicateAlertName(value) && !this.inputContainsInvalidChars(value);
    }

    inputContainsInvalidChars(input: string) {
        /* Test if input contains anything other than
         * alphanumeric characters or - or :
         * and display the relevant error if so.
         * Prevents malicious input such as script tags.
         */

        const regex: RegExp = /[^\u00C0-\u024F\u1E00-\u1EFF\w\d\s:-]/;
        const invalidInput = regex.test(input);
        if (invalidInput) {
            this.validationError = this.translationService.t('AlertEditor.InvalidChars');
        }
        return invalidInput;
    }

    phoneNumberIsValid(phoneNumber: string): boolean {
        
        return this.isInternationalPhoneNumberValid(phoneNumber);

    }

    isInternationalPhoneNumberValid(phoneNumber: string): boolean {
        const regex: RegExp = /^\+(?:[0-9] ?){6,14}[0-9]$/;

        return regex.test(phoneNumber);
    }

    addRecipient(): void {
        this.clearRecipientAddError();

        let newRecipient: IAlertRuleRecipient = copy(this.newRecipient);
        const currentRecipientType: RecipientType = newRecipient?.type;
        const currentRecipientDeliveryType: RecipientDeliveryType = newRecipient?.RecipientType;

        this.replaceEmailRecipientWithUser(newRecipient);

        const recipientValidation: IRecipientValidation = this.isValidRecipient(newRecipient);

        if (!recipientValidation.valid) {
            const retainCustomValue: boolean = (currentRecipientType == RecipientType.Custom);
            const errorReason: string = (recipientValidation?.error) ? recipientValidation?.error : this.translationService.t(`AlertEditor.errorAddingRecipient`);
            this.recipientAddError = `${errorReason}`;

            this.resetNewRecipientPlaceholder(currentRecipientType, currentRecipientDeliveryType, retainCustomValue);
            return;
        }

        this.addLabelsToRecipient(newRecipient);
        newRecipient.deliveryTypeTooltip = this.getRecipientDeliveryTypeTooltip(newRecipient);

        this.dirtyAlertRule.Recipients.push(newRecipient);
        this.resetNewRecipientPlaceholder(currentRecipientType, currentRecipientDeliveryType);
        this.updateConversationalSegments();
    }

    removeRecipient(recipient: IAlertRuleRecipient): void {
        this.dirtyAlertRule.Recipients = this.dirtyAlertRule?.Recipients.filter(currentRecipient => currentRecipient !== recipient);
        this.updateConversationalSegments();
    }

    onChangeRecipientType(type: RecipientType): void {
        this.resetNewRecipientPlaceholder(type, this.newRecipient.RecipientType);
    }

    onChangeRecipientDeliveryType(deliveryType: RecipientDeliveryType): void {
        this.resetNewRecipientPlaceholder(this.newRecipient.type, deliveryType);
    }

    onRecipientUserChange(recipient: IAlertRuleRecipient, $match: IAutocompleteMatch): void {
        if (!recipient || !$match?.objectId) {
            return;
        }

        recipient.UserId = $match.objectId;
    }

    onRecipientGroupChange(recipient: IAlertRuleRecipient, $match: IAutocompleteMatch): void {
        if (!recipient || !$match?.objectId) {
            return;
        }

        recipient.GroupId = $match.objectId;
    }

    onTimeRangeFromChanged(hour: number, minute: number): void {
        this.timeRange.from.hour = hour;
        this.timeRange.from.minute = minute;
        this.updateStartMinuteOfDay(this.timeRange.from);

        this.generateExclusionTimePeriodsForAlert(this.dirtyAlertRule);
    }

    onTimeRangeToChanged(hour: number, minute: number): void {
        this.timeRange.to.hour = hour;
        this.timeRange.to.minute = minute;
        this.updateEndMinuteOfDay(this.timeRange.to);

        this.generateExclusionTimePeriodsForAlert(this.dirtyAlertRule);
    }

    selectedFilterChange(filter: IAlertFilterCriteria, $match: IAutocompleteMatch) {
        filter.All = false;
        filter.GroupId = null;
        filter.ResourceId = $match?.objectId;
    }

    selectedVehicleGroupChange(filter: IAlertFilterCriteria, $match: IAutocompleteMatch) {
        filter.All = false;
        filter.GroupId = $match?.objectId;
        filter.ResourceId = null;
    }

    onFilterTypeUpdate(filter: IAlertFilterCriteria): void {

        switch (filter.filterType as any) {            
            case "1":
                filter.ResourceId = null;
                break;
            case "2":
                filter.GroupId = null;
                break;
            default:
                filter.ResourceId = null;
                filter.GroupId = null;
                break;
        }
    }

    deleteRule() {
        this.alertsService
            .deleteRule(this.alertRule)
            .then(() => {
                if (this.onAlertEditorDeleted) {
                    this.onAlertEditorDeleted();
                }
            });
    }

    saveRule() {
        this.isSavingAlertRule = true;

        this.dirtyAlertRule.FirstNotificationDelay = (this.dirtyAlertRule.FirstNotificationDelay === 0) ? null : this.dirtyAlertRule.FirstNotificationDelay;
        this.dirtyAlertRule.ResendMinuteInterval = (this.dirtyAlertRule.ResendMinuteInterval === 0) ? null : this.dirtyAlertRule.ResendMinuteInterval;
        this.dirtyAlertRule.AlertRuleExclusionTimePeriods?.forEach(period => period.TimeZoneId = this.dateService.getLocalTimezone());

        this.runConversionForModel();
        this.setAlertRulePreferences();

        this.alertsService
            .saveRule(this.dirtyAlertRule)
            .then(alert => {
                this.alertRule = alert;
                this.dirtyAlertRule = copy(alert);

                if (this.onAlertEditorSaved) {
                    this.onAlertEditorSaved();
                }
            })
            .finally(() => {
                this.runConversionForView();
                this.isSavingAlertRule = false;
            });
    }

    getNotificationStatus(notificationStatus: number): string {
        if (notificationStatus in NotificationRecordStatus) {
            return NotificationRecordStatus[notificationStatus];
        }
        return "";
    }

    getRecipientType(recipientType: number): string {
        if (recipientType in NotificationRecipientType) {
            return NotificationRecipientType[recipientType];
        }
        return "";
    }


    toggleDayOfWeek(toggledDayOfWeek: DayOfWeek, forceOverride: boolean = false, overrideValue: boolean = false): void {
        const newExclusionDays: boolean[] = [];

        this.daysOfWeek?.forEach((dayOfWeek, index) => {
            const currentDayOfWeekValue: any = dayOfWeek.value;
            const isToggledDay: boolean = toggledDayOfWeek == currentDayOfWeekValue;

            if (isToggledDay) {
                newExclusionDays[index] = (forceOverride) ? overrideValue : !this.alertExclusionDays[index];
            } else {
                const existingDayValue: boolean | undefined = this.alertExclusionDays[index];
                newExclusionDays[index] = (existingDayValue === undefined) ? false : this.alertExclusionDays[index];
            }
        });

        this.alertExclusionDays = [...newExclusionDays];

        if (!forceOverride) {
            this.generateExclusionTimePeriodsForAlert(this.dirtyAlertRule);
        }
    }

    selectDaysOfWeek(includedDaysOfWeek: DayOfWeek[]): void {
        const excludedDaysOfWeek: DayOfWeek[] = this.allDays.filter(day => includedDaysOfWeek.indexOf(day) === -1);

        includedDaysOfWeek.forEach(dayOfWeek => {
            this.toggleDayOfWeek(dayOfWeek, true, false);
        });

        excludedDaysOfWeek.forEach(dayOfWeek => {
            this.toggleDayOfWeek(dayOfWeek, true, true);
        });

        this.generateExclusionTimePeriodsForAlert(this.dirtyAlertRule);
    }

    cancel(): void {
        if (this.onAlertEditorCancelled) {
            this.onAlertEditorCancelled();
        }
    }

    updateDirtyAlertRuleStatus() {
        this.dirtyAlertRule.Status = this.alertIsActive ? AlertRuleStatus.Active : AlertRuleStatus.Paused;
    }

    //-----

    private setDefaultValueOfCategoryTrigger(): void {
        const triggerKeys: any[] = Object.keys(this.alertCategoryTriggers[this.dirtyAlertRule.Category].enum);

        this.dirtyAlertRule.SubCategory = triggerKeys[0];
        this.getSubCategoryUnitsList();
    }

    private updateResendMinuteInterval(intervalType: ResendIntervalType, minuteInterval: number): void {
        this.dirtyAlertRule.ResendMinuteInterval = (intervalType == ResendIntervalType.Minutes) ? minuteInterval : minuteInterval * 60;

        this.updateConversationalSegments();
    }

    private updateConversationalSegments(): void {
        const newConversationalSegments: IConversationalSegment[] = [];

        if (!this.dirtyAlertRule) {
            return;
        }

        if (AlertCategory[this.dirtyAlertRule.Category]) {
            newConversationalSegments.push({
                styleClass: `value`,
                label: this.translationService.t(`AlertCategoryEnum.${this.AlertCategory[this.dirtyAlertRule?.Category]}`)
            });
        }

        if (this.alertCategoryTriggers[this.dirtyAlertRule.Category] && this.alertCategoryTriggers[this.dirtyAlertRule.Category].enum[this.dirtyAlertRule.SubCategory]) {
            newConversationalSegments.push({
                styleClass: `conditional`,
                label: this.translationService.t(`AlertEditor.conversationalWordTriggers`)
            });

            newConversationalSegments.push({
                styleClass: `value`,
                label: this.translationService.t(
                    `${this.alertCategoryTriggers[this.dirtyAlertRule.Category].enumName}Enum.${this.alertCategoryTriggers[this.dirtyAlertRule.Category].enum[this.dirtyAlertRule.SubCategory]}`
                )
            });
        }

        if (this.dirtyAlertRule.FirstNotificationDelay > 0) {
            newConversationalSegments.push({
                styleClass: `conditional`,
                label: this.translationService.t(`AlertEditor.conversationalWordAfter`)
            });

            newConversationalSegments.push({
                styleClass: `value`,
                label: this.translationService.t(`AlertEditor.conversationalDelayMinutes`, this.dirtyAlertRule.FirstNotificationDelay)
            });
        }

        if (this.dirtyAlertRule.Recipients?.length) {
            newConversationalSegments.push({
                styleClass: `conditional`,
                label: this.translationService.t(`AlertEditor.conversationalWordSendingTo`)
            });

            const recipientLabel = `${this.dirtyAlertRule.Recipients[0].primaryLabel}`;
            const totalRecipientCount = this.getTotalRecipientCount(this.dirtyAlertRule.Recipients);
            const totalOtherCount = totalRecipientCount - 1;

            const otherWordage = this.translationService.t(`AlertEditor.conversationalWordOther`, totalOtherCount);
            const recipientMoreLabel = (totalRecipientCount > 1)
                ? this.translationService.t(`Common.AndXY`, [totalOtherCount, otherWordage])
                : ``;

            const finalLabel = `${recipientLabel} ${recipientMoreLabel}`;

            newConversationalSegments.push({
                styleClass: `value`,
                label: finalLabel
            });
        }

        if (this.dirtyAlertRule.ResendAlerts && this.dirtyAlertRule.ResendMinuteInterval > 0) {
            newConversationalSegments.push({
                styleClass: `conditional`,
                label: this.translationService.t(`AlertEditor.conversationalWordResendingEvery`)
            });

            const pluralNumber: number = (this.resendIntervalType == ResendIntervalType.Minutes)
                ? this.dirtyAlertRule.ResendMinuteInterval
                : this.dirtyAlertRule.ResendMinuteInterval / 60;

            const pluralWord = (this.resendIntervalType == ResendIntervalType.Minutes)
                ? this.translationService.t(`AlertEditor.resendIntervalTypeMinutes`, pluralNumber)
                : this.translationService.t(`AlertEditor.resendIntervalTypeHours`, pluralNumber);

            newConversationalSegments.push({
                styleClass: `value`,
                label: `${pluralNumber} ${pluralWord}`
            });
        }

        if (this.weekDaysDescriptor) {
            newConversationalSegments.push({
                styleClass: `conditional`,
                label: this.translationService.t(`AlertEditor.conversationalWordOn`)
            });

            newConversationalSegments.push({
                styleClass: `value`,
                label: `${this.weekDaysDescriptor}`
            });
        }

        newConversationalSegments.forEach((segment, index) => segment.id = index);

        this.conversationalSegments = newConversationalSegments;
    }

    private getTotalRecipientCount(recipients: IAlertRuleRecipient[] = []): number {
        let recipientCount = 0;

        recipients?.forEach((recipient, index) => {
            if (recipient?.GroupId && index > 0) {
                recipientCount += this.getUserCountFromGroupId(recipient.GroupId);
            } else {
                recipientCount++;
            }
        });

        return recipientCount;
    }

    private getUserCountFromGroupId(groupId: number): number {
        const group: IGroupWithIds = this.groups.find(group => group.Id === groupId);

        return group?.userIds?.length;
    }

    private getData(): angular.IPromise<any> {
        const alertRulesPromise = this.alertsService.getActiveAlertRules();
        const groupsPromise = this.groupService.getGroupsWithFullMappings();
        const usersPromise = this.usersService.getUsers({ includeAdmins: true });
        const vehiclesPromise = this.vehicleService.getVehicles({ includePeripheralMappings: true });
        const timezonesPromise = this.preferencesService.getTimezones();
        const localesPromise = this.translationService.getListOfLocales();

        return this.$q
            .all([groupsPromise, usersPromise, vehiclesPromise, alertRulesPromise, timezonesPromise, localesPromise ])
            .then(([groups, users, vehicles, alertRules, timezones, locales]) => {
                this.groups = groups;
                this.users = users;                 
                this.vehicles = vehicles;
                this.existingAlertRules = alertRules;
                this.timezones = timezones;
                this.locales = locales.filter(locale => locale.Locale != "qa");
            });
    }          

    private resetDelayAndResendIfNeeded(): void {
        const alertCategory = this.dirtyAlertRule?.Category;

        if (!alertCategory) {
            return;
        }

        const alertCategoryTrigger = this.alertCategoryTriggers[this.dirtyAlertRule.Category]

        if (!alertCategoryTrigger) {
            return;
        }

        const categoryAllowedDelayResend = alertCategoryTrigger.categoryAllowedDelayResend;
        const categoryTriggerAllowedDelayResend = alertCategoryTrigger.categoryAllowedDelayResend || alertCategoryTrigger.subCategoriesAllowedDelayResend?.[this.dirtyAlertRule?.SubCategory]

        if (!categoryAllowedDelayResend && !categoryTriggerAllowedDelayResend) {
            this.resetAlertDelayAndResend(this.dirtyAlertRule);
        }
    }

    private resetAlertDelayAndResend(alertRule: IAlertRule): void {
        if (!alertRule) {
            return;
        }

        this.enableDelay = false;
        this.resendMinuteInterval = 0;

        alertRule.ResendAlerts = false;
        alertRule.ResendMinuteInterval = null;
        alertRule.FirstNotificationDelay = null;
    }

    private availablePlotReasonsForAlerting: string[] = ["IgnitionOn", "IgnitionOff"];

    private setupEnumArrays(): void {
        this.reasonCodes = EnumService.enumToArray("ReasonCode", ReasonCode);

        this.reasonCodes = this.reasonCodes.filter(code => {
            return this.availablePlotReasonsForAlerting.includes(code.name);
        });
        this.reasonCodes.sort((reasonCode1, resaonCode2) => (reasonCode1.value > resaonCode2.value) ? 1 : -1);

        this.alertCategories = EnumService.enumToArray("AlertCategory", AlertCategory);
        this.alertCategories.sort((category1, category2) => (category1.value > category2.value) ? 1 : -1);

        this.daysOfWeek = EnumService.enumToArray("DayOfWeek", DayOfWeek);
        this.daysOfWeek.sort((day1, day2) => (day1.value > day2.value) ? 1 : -1);

        this.recipientTypes = EnumService.enumToArray("RecipientType", RecipientType);
        this.recipientTypes.sort((recipientType1, recipientType2) => (recipientType1.value > recipientType2.value) ? 1 : -1);

        this.recipientDeliveryTypes = EnumService.enumToArray("RecipientDeliveryType", RecipientDeliveryType);
        this.recipientDeliveryTypes.sort((recipientDeliveryType1, recipientDeliveryType2) => (recipientDeliveryType1.value > recipientDeliveryType2.value) ? 1 : -1);

        this.distanceUnitTypes = EnumService.enumToArray("DistanceMeasurement", DistanceMeasurement);
        this.distanceUnitTypes.sort((distanceUnitType1, distanceUnitType2) => (distanceUnitType1.value > distanceUnitType2.value) ? 1 : -1);

        this.temperatureUnitTypes = EnumService.enumToArray("TemperatureMeasurement", TemperatureMeasurement);
        this.temperatureUnitTypes.sort((temperatureUnitType1, temperatureUnitType2) => (temperatureUnitType1.value > temperatureUnitType2.value) ? 1 : -1);

        this.volumeUnitTypes = EnumService.enumToArray("VolumeMeasurement", VolumeMeasurement);
        this.volumeUnitTypes.sort((volumeUnitType1, volumeUnitType2) => (volumeUnitType1.value > volumeUnitType2.value) ? 1 : -1);

        this.currencyTypes = EnumService.enumToArray("Currency", Currency);
        this.currencyTypes.sort((currencyType1, currencyType2) => (currencyType1.value > currencyType2.value) ? 1 : -1);
    }

    private setupRule(): void {
        this.getData().finally(() => {

            if (!this.alertRule) {
                this.alertRule = this.createDefaultRule();
                this.setDefaultAlertRulePreferences();
            }

            this.setDelayResendIcon();

            this.resetNewRecipientPlaceholder(RecipientType.Group, RecipientDeliveryType.Email);

            this.getSubCategoryUnitsList();
            this.processAlertRule(this.alertRule);

            this.setUIAlertPreferences();
            this.runConversionForView();


            this.isFormReady = true;
        });
    }

    private clearRecipientAddError(): void {
        this.recipientAddError = ``;
    }

    private replaceEmailRecipientWithUser(recipient: IAlertRuleRecipient): void {
        if (recipient?.RecipientType !== RecipientDeliveryType.Email) {
            return;
        }

        const user: IUser = this.users.find(user => user.Email?.toLowerCase()?.trim() === recipient?.Custom?.toLowerCase()?.trim());

        if (user && user?.Id) {
            recipient.RecipientType = RecipientDeliveryType.Email;
            recipient.type = RecipientType.User;
            recipient.UserId = user.Id;
        }
    }

    private isDuplicateRecipient(recipient: IAlertRuleRecipient): boolean {
        const isDuplicateGroup: boolean = (recipient?.type == 0) && this.dirtyAlertRule.Recipients?.filter(ruleRecipient => ruleRecipient?.type == 0 && ruleRecipient?.GroupId === recipient?.GroupId)?.length > 0;
        const isDuplicateUser: boolean = (recipient?.type == 1) && this.dirtyAlertRule.Recipients?.filter(ruleRecipient => ruleRecipient?.type == 1 && ruleRecipient.RecipientType == recipient.RecipientType && ruleRecipient?.UserId === recipient?.UserId)?.length > 0;
        const isDuplicateCustom: boolean = (recipient?.type == 2) && this.dirtyAlertRule.Recipients?.filter(ruleRecipient => ruleRecipient?.type == 2 && ruleRecipient?.Custom === recipient?.Custom)?.length > 0;

        return isDuplicateGroup || isDuplicateUser || isDuplicateCustom;
    }

    private addLabelsToRecipient(recipient: IAlertRuleRecipient): void {
        if (!recipient || !RecipientType[recipient?.type]) {
            return null;
        }

        if (recipient.type == RecipientType.Group) {
            const group = this.groups.find(group => group.Id === recipient?.GroupId);
            const userCount = group?.userIds?.length || 0;
            const label = `${userCount} ${this.translationService.t(`AlertEditor.recipientGroupUsers`, userCount)}`;
            
            recipient.primaryLabel = `${group?.Name}`;
            recipient.secondaryLabel = `${label}`;

        } else if (recipient.type == RecipientType.User) {
            const user: IUser = this.users.find(user => user.Id === recipient?.UserId);
            const emailString: string = (recipient?.RecipientType === RecipientDeliveryType.Email) ? `${user?.Email}` : ``;

            recipient.primaryLabel = user?.CurrentAccountUserMapping?.Name ? `${user?.CurrentAccountUserMapping.Name}` : `${emailString}`;
            recipient.secondaryLabel = user?.CurrentAccountUserMapping?.Name ? `${emailString}` : ``;

        } else if (recipient.type == RecipientType.Custom) {
            recipient.primaryLabel = `${recipient?.Custom}`;
            recipient.secondaryLabel = ``;

        } else {
            return null;

        }
    }

    private getRecipientDeliveryTypeTooltip(recipient: IAlertRuleRecipient): string {
        if (!RecipientDeliveryType[recipient?.RecipientType]) {
            return null;
        }

        if (recipient.RecipientType === RecipientDeliveryType.Email) {
            return this.translationService.t(`AlertEditor.sendingEmail`);

        } else if (recipient.RecipientType === RecipientDeliveryType.Sms) {
            return this.translationService.t(`AlertEditor.sendingSms`);

        }
    }

    private setAlertRulePreferences() {
        
        for (let prefType of Object.values(AlertPreferenceType)) {

            const prefTypeId:number = AlertPreferenceType[prefType];

            if (!this.alertPreferences[prefTypeId]) {
                continue;
            }

            let index = this.dirtyAlertRule.Preferences.findIndex(pref => pref.PreferenceTypeId === prefTypeId);
            if (index == -1) {
                this.dirtyAlertRule.Preferences.push({
                    Value: this.alertPreferences[prefTypeId],
                    PreferenceTypeId: prefTypeId
                })
            }
            else {
                this.dirtyAlertRule.Preferences[index].Value = this.alertPreferences[prefTypeId]
            }

        }
       
    }

    private setUIAlertPreferences() {
        
        if (this.dirtyAlertRule != null) {
            this.dirtyAlertRule.Preferences.forEach(preference => {

                let prefType;

                switch (preference.PreferenceTypeId) {
                    case AlertPreferenceType.DistanceMeasurement:
                        prefType = AlertPreferenceType.DistanceMeasurement;
                        break;
                    case AlertPreferenceType.VolumeMeasurement:
                        prefType = AlertPreferenceType.VolumeMeasurement;
                        break;
                    case AlertPreferenceType.TemperatureMeasurement:
                        prefType = AlertPreferenceType.TemperatureMeasurement;
                        break;
                    case AlertPreferenceType.Currency:
                        prefType = AlertPreferenceType.Currency;
                        break;
                    case AlertPreferenceType.Language:
                        prefType = AlertPreferenceType.Language;
                        break;
                    case AlertPreferenceType.Timezone:
                        prefType = AlertPreferenceType.Timezone;
                        break;
                }

                this.alertPreferences[prefType] = preference.Value;

            });
        } else {// set default user preferences
            if (!this.combinedPreferences) {

                this.alertPreferences[AlertPreferenceType.DistanceMeasurement] = this.combinedPreferences.DistanceMeasurement.Value.toString();
                this.alertPreferences[AlertPreferenceType.VolumeMeasurement] = this.combinedPreferences.VolumeMeasurement.Value.toString();
                this.alertPreferences[AlertPreferenceType.TemperatureMeasurement] = this.combinedPreferences.TemperatureMeasurement.Value.toString();
                this.alertPreferences[AlertPreferenceType.Currency] = this.combinedPreferences.Currency.Value.toString();
                this.alertPreferences[AlertPreferenceType.Language] = this.combinedPreferences.Language.Value.toString();
                this.alertPreferences[AlertPreferenceType.Timezone] = this.combinedPreferences.Timezone.Value.toString();
                return;
            }

 
        }
    }


    private isValidRecipient(recipient: IAlertRuleRecipient): IRecipientValidation {
        let recipientValidation: IRecipientValidation = {
            valid: true
        };

        if (recipient?.type == RecipientType.Group && !this.isGroupIdValid(recipient?.GroupId)) {
            recipientValidation.error = this.translationService.t(`AlertEditor.groupNotValid`);
            recipientValidation.valid = false;

        } else if (recipient?.type == RecipientType.User && !this.isUserIdValid(recipient?.UserId)) {
            recipientValidation.error = this.translationService.t(`AlertEditor.userNotValid`);
            recipientValidation.valid = false;

        } else if (recipient?.type == RecipientType.Custom) {

            if (recipient?.RecipientType == RecipientDeliveryType.Email) {
                if (recipient?.Custom == null) {
                    recipientValidation.error = this.translationService.t(`AlertEditor.emailNotProvided`);
                    recipientValidation.valid = false;

                } else if (!this.validationService.emailIsValid(recipient?.Custom)) {
                    recipientValidation.error = this.translationService.t(`AlertEditor.emailNotValid`);
                    recipientValidation.valid = false;
                }
            } else if (recipient?.RecipientType == RecipientDeliveryType.Sms) {
                if (recipient?.Custom == null) {
                    recipientValidation.error = this.translationService.t(`AlertEditor.phoneNotProvided`);
                    recipientValidation.valid = false;

                } else if (!this.phoneNumberIsValid(recipient?.Custom)) {
                    recipientValidation.error = this.translationService.t(`AlertEditor.phoneNotValid`);
                    recipientValidation.valid = false;
                }
            }
        }

        if (this.isDuplicateRecipient(recipient)) {
            recipientValidation.error = this.translationService.t(`AlertEditor.recipientAlreadyListed`);
            recipientValidation.valid = false;
        }

        return recipientValidation;
    }

    private isGroupIdValid(groupId: number): boolean {
        if (!groupId) {
            return false;
        }

        return this.groups.find(group => group.Id === groupId) !== null;
    }

    private isUserIdValid(userId: number): boolean {
        if (!userId) {
            return false;
        }

        return this.users.find(user => user.Id === userId) !== null;
    }

    private resetNewRecipientPlaceholder(currentRecipientType: RecipientType, currentRecipientDeliveryType: RecipientDeliveryType, retainPreviousCustomValue: boolean = false): void {
        this.newRecipient = {
            Id: 0,
            RuleId: this.dirtyAlertRule?.Id,
            GroupId: null,
            UserId: null,
            Custom: (retainPreviousCustomValue) ? this.newRecipient.Custom : null,
            RecipientType: currentRecipientDeliveryType | RecipientDeliveryType.Email,
            countryDialCode: null,
            type: currentRecipientType | RecipientType.Group
        };

        if (!retainPreviousCustomValue) {
            this.newRecipientSmsInput = ``;
        }
    }

    private updateStartMinuteOfDay(range: IHourMinute): void {
        this.filteredStartMinuteOfDay = (range.hour * 60) + range.minute;

        this.validateTimeRange();
    }

    private updateEndMinuteOfDay(range: IHourMinute): void {
        this.filteredEndMinuteOfDay = (range.hour * 60) + range.minute

        this.validateTimeRange();
    }

    private validateTimeRange(): void {
        this.timeRangeValid = this.filteredStartMinuteOfDay < this.filteredEndMinuteOfDay;
    }

    private getExcludedDaysOfWeek(): DayOfWeek[] {
        const excludedDaysOfWeek: DayOfWeek[] = [];

        this.alertExclusionDays.forEach((exclusionDay, index) => {
            if (exclusionDay) {
                excludedDaysOfWeek.push(index);
            }
        });

        return excludedDaysOfWeek;
    }

    private generateExclusionTimePeriodsForAlert(alertRule: IAlertRule): void {
        if (!alertRule) {
            return;
        }
        
        const excludedDayStartTime: Date = this.dateService.getStartOfDay(new Date());
        const excludedDayEndTime: Date = this.dateService.getEndOfDay(new Date());
        const excludedDaysOfWeek: DayOfWeek[] = this.getExcludedDaysOfWeek();

        const timeRangeToMinuteOfDay: number = (this.timeRange.to.hour * 60) + this.timeRange.to.minute;
        const timeRangeFromMinuteOfDay: number = (this.timeRange.from.hour * 60) + this.timeRange.from.minute;

        const includedDayStartTime: Date = this.dateService.getStartOfDay(new Date());
        includedDayStartTime.setMinutes(timeRangeToMinuteOfDay);

        const includedDayEndTime: Date = this.dateService.getStartOfDay(new Date())
        includedDayEndTime.setMinutes(timeRangeFromMinuteOfDay);

        const includedDaysOfWeek: DayOfWeek[] = this.allDays.filter(day => excludedDaysOfWeek.indexOf(day) === -1);

        const isFullDayPeriod: boolean = (timeRangeToMinuteOfDay + timeRangeFromMinuteOfDay === 1439);

        alertRule.AlertRuleExclusionTimePeriods = [];

        excludedDaysOfWeek?.forEach(dayOfWeek => {
            alertRule.AlertRuleExclusionTimePeriods.push({
                Id: 0,
                AlertRuleId: this.dirtyAlertRule.Id,
                DayOfWeek: dayOfWeek,
                StartTimeAdjusted: excludedDayStartTime,
                EndTimeAdjusted: excludedDayEndTime
            });
        });

        if (!isFullDayPeriod) {
            includedDaysOfWeek?.forEach(dayOfWeek => {
                alertRule.AlertRuleExclusionTimePeriods.push({
                    Id: 0,
                    AlertRuleId: this.dirtyAlertRule.Id,
                    DayOfWeek: dayOfWeek,
                    StartTimeAdjusted: includedDayStartTime,
                    EndTimeAdjusted: includedDayEndTime
                });
            });
        }

        this.updateWeekDaysDescriptor();
    }

    private processAlertRule(alertRule: IAlertRule): void {
        this.dirtyAlertRule = copy(alertRule);

        this.setLocalDelayVars();
        this.setLocalResendVars();
        this.addRecipientLabelsToRecipients(this.dirtyAlertRule?.Recipients);
        this.setInputTypeFilter();
        this.alertIsActive = alertRule.Status === AlertRuleStatus.Active ? true : false;
        this.timeRange = this.getTimeRangeFromAlertRule(this.dirtyAlertRule);
        this.updateStartMinuteOfDay(this.timeRange.from);
        this.updateEndMinuteOfDay(this.timeRange.to);
        this.alertExclusionDays = this.getExclusionsDaysFromAlertRule(this.dirtyAlertRule);
        this.updateWeekDaysDescriptor();

        this.resetDelayAndResendIfNeeded();
        this.updateConversationalSegments();
    }

    private runConversionForView(): void {
        if (!this.dirtyAlertRule) {
            return;
        }

        // speeding
        if (this.dirtyAlertRule.Category == AlertCategory.Vehicle && this.dirtyAlertRule.SubCategory == VehicleAlertSubCategory.Speeding && this.alertPreferences[AlertPreferenceType.DistanceMeasurement] == '1') {
            this.dirtyAlertRule.TriggerValue = this.unitConversionService.convert(this.dirtyAlertRule.TriggerValue, UnitConversionType.Speed, 0, false, parseInt(this.alertPreferences[AlertPreferenceType.DistanceMeasurement]));
        }

        // volume
        if (this.dirtyAlertRule.Category == AlertCategory.Vehicle && this.dirtyAlertRule.SubCategory == VehicleAlertSubCategory.FuelLevelChange && this.alertPreferences[AlertPreferenceType.VolumeMeasurement] != '0') {
            this.dirtyAlertRule.TriggerValue = this.unitConversionService.convert(this.dirtyAlertRule.TriggerValue, UnitConversionType.FuelCapacity, 0, false, parseInt(this.alertPreferences[AlertPreferenceType.VolumeMeasurement]));
        }
    }

    private runConversionForModel(): void {
        if (!this.dirtyAlertRule) {
            return;
        }

        // speeding
        if (this.dirtyAlertRule.Category == AlertCategory.Vehicle && this.dirtyAlertRule.SubCategory == VehicleAlertSubCategory.Speeding && this.alertPreferences[AlertPreferenceType.DistanceMeasurement] == '1') {
            this.dirtyAlertRule.TriggerValue = this.unitConversionService.convert(this.dirtyAlertRule.TriggerValue, UnitConversionType.Speed, 0, true, parseInt(this.alertPreferences[AlertPreferenceType.DistanceMeasurement]));
        }
        // volume
        if (this.dirtyAlertRule.Category == AlertCategory.Vehicle && this.dirtyAlertRule.SubCategory == VehicleAlertSubCategory.FuelLevelChange && this.alertPreferences[AlertPreferenceType.VolumeMeasurement] != '0') {
            this.dirtyAlertRule.TriggerValue = this.unitConversionService.convert(this.dirtyAlertRule.TriggerValue, UnitConversionType.FuelCapacity, 0, true, parseInt(this.alertPreferences[AlertPreferenceType.VolumeMeasurement]));
        }
    }

    private setLocalDelayVars(): void {
        this.enableDelay = this.dirtyAlertRule?.FirstNotificationDelay > 0;
    }

    private setLocalResendVars(): void {
        this.resendIntervalType = (!this.dirtyAlertRule?.ResendMinuteInterval || this.dirtyAlertRule?.ResendMinuteInterval < 60)
            ? ResendIntervalType.Minutes
            : ResendIntervalType.Hours;
        this.resendMinuteInterval = (this.resendIntervalType == ResendIntervalType.Minutes)
            ? this.dirtyAlertRule?.ResendMinuteInterval
            : this.dirtyAlertRule?.ResendMinuteInterval / 60;
    }

    private updateWeekDaysDescriptor(): void {
        const excludedDaysOfWeek: DayOfWeek[] = this.getExcludedDaysOfWeek();
        const includedDaysOfWeek: DayOfWeek[] = this.allDays.filter(day => excludedDaysOfWeek.indexOf(day) === -1);

        const includesWholeWeekend: boolean = this.weekendDays.filter(day => includedDaysOfWeek.indexOf(day) > -1)?.length === 2;
        const includesWholeWorkWeek: boolean = this.weekDays.filter(day => includedDaysOfWeek.indexOf(day) > -1)?.length === 5;
        const isEntireWeek: boolean = includesWholeWeekend && includesWholeWorkWeek;

        if (isEntireWeek) {
            this.weekDaysDescriptor = this.translationService.t(`Common.AllDays`);

        } else if (includesWholeWorkWeek && !includesWholeWeekend && includedDaysOfWeek?.length === 5) {
            this.weekDaysDescriptor = this.translationService.t(`Common.Weekdays`);

        } else if (!includesWholeWorkWeek && includesWholeWeekend && includedDaysOfWeek?.length === 2) {
            this.weekDaysDescriptor = this.translationService.t(`Common.WeekendDays`);

        } else if (includedDaysOfWeek?.length > 2) {
            this.weekDaysDescriptor = this.translationService.t(`AlertEditor.totalSelectedFilterDays`, includedDaysOfWeek.length);

        } else if (includedDaysOfWeek?.length === 2) {
            const day1Translation = this.translationService.t(`DayOfWeekEnum.${this.DayOfWeek[this.DayOfWeek[this.daysOfWeek[includedDaysOfWeek[0]].name]]}`);
            const day2Translation = this.translationService.t(`DayOfWeekEnum.${this.DayOfWeek[this.DayOfWeek[this.daysOfWeek[includedDaysOfWeek[1]].name]]}`);

            this.weekDaysDescriptor = this.translationService.t(`Common.XAndY`, [day1Translation, day2Translation]);

        } else if (includedDaysOfWeek?.length === 1) {
            this.weekDaysDescriptor = this.translationService.t(`DayOfWeekEnum.${this.DayOfWeek[this.DayOfWeek[this.daysOfWeek[includedDaysOfWeek[0]].name]]}`);

        } else {
            this.weekDaysDescriptor = this.translationService.t(`Common.NoDays`);
        }

        this.updateConversationalSegments();
    }

    private getTimeRangeFromAlertRule(alertRule: IAlertRule): ITimeRangeSelection {
        let timeRangeSelection: ITimeRangeSelection = this.createDefaultTimeRangeSelection();
                
        alertRule?.AlertRuleExclusionTimePeriods.forEach((exclusionTimePeriod, index) => {
            const fixedStartTime = this.fixCeEpochTime(new Date(exclusionTimePeriod.StartTimeAdjusted));
            const fixedEndTime = this.fixCeEpochTime(new Date(exclusionTimePeriod.EndTimeAdjusted));

            const startTimeMinuteOfDay: number = (fixedStartTime.getHours() * 60) + fixedStartTime.getMinutes();
            const endTimeMinuteOfDay: number = (fixedEndTime.getHours() * 60) + fixedEndTime.getMinutes();

            const currentTimeRangeFromMinuteOfDay: number = (timeRangeSelection.from.hour * 60) + timeRangeSelection.from.minute;
            const currentTimeRangeToMinuteOfDay: number = (timeRangeSelection.to.hour * 60) + timeRangeSelection.to.minute;

            const isFullDayPeriod: boolean = this.timeRangeIsFullDay(fixedStartTime, fixedEndTime);

            if (isFullDayPeriod) {
                return;
            }

            if (startTimeMinuteOfDay < currentTimeRangeToMinuteOfDay) {
                timeRangeSelection.to.hour = fixedStartTime.getHours();
                timeRangeSelection.to.minute = fixedStartTime.getMinutes();
            }

            if (endTimeMinuteOfDay > currentTimeRangeFromMinuteOfDay) {
                timeRangeSelection.from.hour = fixedEndTime.getHours();
                timeRangeSelection.from.minute = fixedEndTime.getMinutes();
            }
        });

        return timeRangeSelection;
    }

    private fixCeEpochTime(date: Date): Date {
        const fixedTime = new Date();
        const timezoneOffsetMs: number = fixedTime.getTimezoneOffset() * 60 * 1000;

        fixedTime.setHours(
            date.getUTCHours(),
            date.getUTCMinutes(),
            date.getUTCSeconds(),
            date.getUTCMilliseconds()
        );

        return new Date(fixedTime.getTime() - timezoneOffsetMs);
    }

    private createDefaultTimeRangeSelection(): ITimeRangeSelection {
        const newTimeRange: ITimeRangeSelection = {
            from: {
                hour: 0,
                minute: 0
            },
            to: {
                hour: 23,
                minute: 59
            }
        };

        return newTimeRange;
    }

    private getExclusionsDaysFromAlertRule(alertRule: IAlertRule): boolean[] {
        const exclusionDays: boolean[] = [];

        this.daysOfWeek?.forEach((dayOfWeek, index) => {
            exclusionDays[index] = false;

            alertRule?.AlertRuleExclusionTimePeriods.forEach(exclusionTimePeriod => {
                // have we found the correct day
                if (exclusionTimePeriod.DayOfWeek === index) {
                    const fixedStartTime = this.fixCeEpochTime(new Date(exclusionTimePeriod.StartTimeAdjusted));
                    const fixedEndTime = this.fixCeEpochTime(new Date(exclusionTimePeriod.EndTimeAdjusted));

                    const isFullDayExclusion: boolean = this.timeRangeIsFullDay(fixedStartTime, fixedEndTime);

                    if (isFullDayExclusion) {
                        exclusionDays[index] = true;
                    }
                }
            })
        });

        return exclusionDays;
    }

    private addRecipientLabelsToRecipients(recipients: IAlertRuleRecipient[] = []): void {
        recipients.forEach(recipient => {
            this.addLabelsToRecipient(recipient);
            recipient.deliveryTypeTooltip = this.getRecipientDeliveryTypeTooltip(recipient);
        });
    }

    private timeRangeIsFullDay(from: Date, to: Date): boolean {
        const fromMinutes: number = (from.getUTCHours() * 60) + from.getUTCMinutes();
        const toMinutes: number = (to.getUTCHours() * 60) + to.getUTCMinutes();

        return (fromMinutes === 0 && toMinutes >= 1439) || (fromMinutes > toMinutes && fromMinutes - toMinutes <= 1);
    }

    private createDefaultRule(): IAlertRule {
        const newAlertRule: IAlertRule = {
            Id: 0,
            Recipients: [],
            Preferences: [],
            Status: AlertRuleStatus.Active,
            FirstNotificationDelay: null,
            ResendAlerts: false,
            ResendMinuteInterval: null,
            Category: AlertCategory.Geofence,
            SubCategory: 0,
            TriggerValueOperator: TriggerValueOperator.GreaterThan,
            Filters: [
                { filterType: ResourceFilterType.Any, ResourceType: ResourceType.Geofence },
                { filterType: ResourceFilterType.Any, ResourceType: ResourceType.Vehicle },
                { filterType: ResourceFilterType.Any, ResourceType: ResourceType.Driver },
                { filterType: ResourceFilterType.Specific, ResourceType: ResourceType.InputType }
            ],
            AlertRuleExclusionTimePeriods: []
        };

        return newAlertRule;
    }

    private setDefaultAlertRulePreferences() {


        this.alertRule.Preferences.push({
            Value: this.combinedPreferences.DistanceMeasurement.Value.toString(),
            PreferenceTypeId: AlertPreferenceType.DistanceMeasurement
        })

        this.alertRule.Preferences.push({
            Value: this.combinedPreferences.VolumeMeasurement.Value.toString(),
            PreferenceTypeId: AlertPreferenceType.VolumeMeasurement
        })

        this.alertRule.Preferences.push({
            Value: this.combinedPreferences.TemperatureMeasurement.Value.toString(),
            PreferenceTypeId: AlertPreferenceType.TemperatureMeasurement
        })

        this.alertRule.Preferences.push({
            Value: this.combinedPreferences.Currency.Value.toString(),
            PreferenceTypeId: AlertPreferenceType.Currency
        })

        this.alertRule.Preferences.push({
            Value: this.combinedPreferences.Language.Value.toString(),
            PreferenceTypeId: AlertPreferenceType.Language
        })

        this.alertRule.Preferences.push({
            Value: this.combinedPreferences.Timezone.Value.toString(),
            PreferenceTypeId: AlertPreferenceType.Timezone
        })
    }

    private setDelayResendIcon() {
        if (this.dirtyAlertRule
            && !this.alertCategoryTriggers[this.dirtyAlertRule.Category].categoryAllowedDelayResend
            && !this.alertCategoryTriggers[this.dirtyAlertRule.Category].subCategoriesAllowedDelayResend?.[this.dirtyAlertRule.SubCategory]) {
            this.delayResendIcon = 'lock';
            return;
        }

        this.delayResendIcon = 'update';
    }

    private setInputTypeFilter(): void {
        if (!this.dirtyAlertRule) return;

        this.inputTypeFilter = null;

        if (this.dirtyAlertRule.Category == AlertCategory.Inputs)
            this.inputTypeFilter = this.dirtyAlertRule.Filters.find(x => x.ResourceType == ResourceType.InputType);
    }

    private hasForDuplicateAlertName(name: string): boolean {
        name = name?.toLowerCase()?.trim();
        this.validationError = this.translationService.t('AlertEditor.formFieldNameExists');
        return !!this.existingAlertRules.find(rule => rule.Name?.toLowerCase()?.trim() === name && rule.Id !== this.dirtyAlertRule.Id);
    }

    private getSubCategoryUnitsList() {
        
        if (this.dirtyAlertRule == null || !this.alertCategoryTriggers[this.dirtyAlertRule.Category].subCategoriesUnits || !this.alertCategoryTriggers[this.dirtyAlertRule.Category].subCategoriesUnits[this.dirtyAlertRule.SubCategory])
            return;

        switch (this.alertCategoryTriggers[this.dirtyAlertRule.Category].subCategoriesUnits[this.dirtyAlertRule.SubCategory]){

            case AlertPreferenceType.DistanceMeasurement:

                this.subCategoryUnits = this.distanceUnitTypes;
                break;

            case AlertPreferenceType.VolumeMeasurement:

                this.subCategoryUnits = this.volumeUnitTypes;
                break;

            case AlertPreferenceType.TemperatureMeasurement:

                this.subCategoryUnits = this.temperatureUnitTypes;
                break;

            case AlertPreferenceType.Currency:

                this.subCategoryUnits = this.currencyTypes;
                break;
        }
    }

}

export class AlertEditorComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            alertRule: "<",
            autoLoadedRecordId: "<?",
            onAlertEditorCancelled: "&",
            onAlertEditorDeleted: "&",
            onAlertEditorSaved: "&"
        };

        this.controller = AlertEditorComponentController;
        this.template = require("./alertEditor.component.html");
    }
}
